import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getLeaderboard, type LeaderboardRow, type Sex } from "../lib/api";
import { isLang, makeT } from "../i18n/context";
import type { Lang } from "../i18n";
import LogoTile from "../components/LogoTile";

const POLL_MS = 5000;
const SPOTLIGHT_MS = 6000;
const TOP_N = 10;
const LOCALE: Record<Lang, string> = { kk: "kk-KZ", ru: "ru-RU", en: "en-US" };

const rowKey = (r: LeaderboardRow) => `${r.nickname ?? "—"}|${r.sex ?? "—"}`;

// Fun distance analogies for the finisher spotlight. Landmark heights/lengths in
// metres; we pick the biggest one that still gives a multiplier ≥ 1.5 so the
// number reads nicely (e.g. 190 m ≈ "1.8 × football field").
const LANDMARKS: { m: number; emoji: string; ru: string; kk: string; en: string }[] = [
  { m: 828, emoji: "🏙️", ru: "Бурдж-Халифа", kk: "Бурдж-Халифа", en: "Burj Khalifa" },
  { m: 330, emoji: "🗼", ru: "Эйфелева башня", kk: "Эйфель мұнарасы", en: "the Eiffel Tower" },
  { m: 105, emoji: "⚽", ru: "футбольное поле", kk: "футбол алаңы", en: "a football pitch" },
  { m: 76, emoji: "✈️", ru: "Boeing 747", kk: "Boeing 747", en: "a Boeing 747" },
  { m: 30, emoji: "🐋", ru: "синий кит", kk: "көк кит", en: "a blue whale" },
];
const ANALOGY_LEAD: Record<Lang, string> = {
  ru: "Это как",
  kk: "Бұл шамамен",
  en: "That's about",
};
function distanceAnalogy(distanceM: number, lang: Lang): string {
  const byBig = [...LANDMARKS].sort((a, b) => b.m - a.m);
  const pick = byBig.find((l) => distanceM / l.m >= 1.5) ?? byBig[byBig.length - 1];
  const mult = distanceM / pick.m;
  const n = mult >= 10 ? String(Math.round(mult)) : String(Math.round(mult * 10) / 10);
  return `${ANALOGY_LEAD[lang]} ${n} × ${pick[lang]} ${pick.emoji}`;
}

// Big-scale landmarks for the COLLECTIVE total distance (everyone combined).
const BIG_LANDMARKS: { m: number; emoji: string; ru: string; kk: string; en: string }[] = [
  { m: 4_700_000, emoji: "🌍", ru: "река Конго", kk: "Конго өзені", en: "the Congo river" },
  { m: 4_248_000, emoji: "🌊", ru: "река Иртыш", kk: "Ертіс өзені", en: "the Irtysh river" },
  { m: 1_200_000, emoji: "🚗", ru: "путь Алматы–Астана", kk: "Алматы–Астана жолы", en: "the Almaty–Astana drive" },
  { m: 42_195, emoji: "🏃", ru: "марафон", kk: "марафон", en: "a marathon" },
  { m: 400, emoji: "🏟️", ru: "круг стадиона", kk: "стадион шеңбері", en: "a stadium lap" },
];
function bigDistanceAnalogy(totalM: number, lang: Lang): string {
  const byBig = [...BIG_LANDMARKS].sort((a, b) => b.m - a.m);
  const pick = byBig.find((l) => totalM / l.m >= 0.8) ?? byBig[byBig.length - 1];
  const mult = totalM / pick.m;
  const n = mult >= 10 ? String(Math.round(mult)) : String(Math.round(mult * 10) / 10);
  return `${ANALOGY_LEAD[lang]} ${n} × ${pick[lang]} ${pick.emoji}`;
}

interface Spotlight {
  name: string;
  sex: Sex;
  power: number;
  distance: number;
  overallRank: number;
  analogy: string;
}

// TV-ориентированный лидерборд станции «Гребля». Публичная страница:
// открывается по ссылке на смарт-ТВ, поллит /leaderboard, делит результаты на
// мужской/женский топ-10 и при вводе тренером нового результата показывает
// «спотлайт» атлета на весь экран, затем возвращается к борду.
export default function Leaderboard() {
  // Язык из ?lang= (kk|ru|en); по умолчанию — казахский (приоритет на ТВ).
  const lang: Lang = useMemo(() => {
    const q = new URLSearchParams(window.location.search).get("lang");
    return isLang(q) ? q : "kk";
  }, []);
  const t = useMemo(() => makeT(lang), [lang]);

  const [rows, setRows] = useState<LeaderboardRow[]>([]);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [offline, setOffline] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [flashKeys, setFlashKeys] = useState<Set<string>>(new Set());
  const [spotlight, setSpotlight] = useState<Spotlight | null>(null);

  const aliveRef = useRef(true);
  const prevPower = useRef<Map<string, number>>(new Map());
  const spotQueue = useRef<Spotlight[]>([]);
  const [spotPing, setSpotPing] = useState(0);

  const tick = useCallback(async () => {
    try {
      const r = await getLeaderboard();
      if (!aliveRef.current) return;
      const next = r.leaderboard ?? [];

      // Что изменилось/появилось с прошлого опроса.
      const changed: LeaderboardRow[] = [];
      const snapshot = new Map<string, number>();
      for (const row of next) {
        const k = rowKey(row);
        snapshot.set(k, row.power_w);
        const before = prevPower.current.get(k);
        if (before === undefined || before !== row.power_w) changed.push(row);
      }
      const isFirst = prevPower.current.size === 0;
      prevPower.current = snapshot;

      setRows(next);
      setUpdatedAt(new Date());
      setOffline(false);
      setHasData(true);

      if (!isFirst && changed.length) {
        setFlashKeys(new Set(changed.map(rowKey)));
        setTimeout(() => aliveRef.current && setFlashKeys(new Set()), 1500);

        // Очередь спотлайтов: показываем каждого только что финишировавшего
        // (обычно тройку) с местом в ОБЩЕМ зачёте и весёлой аналогией дистанции.
        const overall = [...next].sort((a, b) => b.power_w - a.power_w);
        const spots: Spotlight[] = changed
          .filter((r) => r.sex === "M" || r.sex === "F")
          .sort((a, b) => ((a.recorded_at ?? "") < (b.recorded_at ?? "") ? -1 : 1))
          .map((r) => ({
            name: r.nickname || t("coach.rowing.guestFallback"),
            sex: r.sex as Sex,
            power: Math.round(r.power_w),
            distance: r.distance_m,
            overallRank: overall.findIndex((x) => rowKey(x) === rowKey(r)) + 1,
            analogy: distanceAnalogy(r.distance_m, lang),
          }));
        spotQueue.current.push(...spots);
        if (spotQueue.current.length > 6) {
          spotQueue.current = spotQueue.current.slice(-6);
        }
        setSpotPing((x) => x + 1);
      }
    } catch {
      if (aliveRef.current) setOffline(true);
    }
  }, [t, lang]);

  useEffect(() => {
    aliveRef.current = true;
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      aliveRef.current = false;
      clearInterval(id);
    };
  }, [tick]);

  // Проигрываем очередь спотлайтов по одному: показать → подождать SPOTLIGHT_MS →
  // спрятать → следующий. Два эффекта: первый достаёт из очереди, когда экран
  // свободен (триггер — конец показа или spotPing), второй владеет таймером
  // скрытия. Держать таймер в первом нельзя: его cleanup сработал бы сразу же
  // от собственного setSpotlight и отменил скрытие.
  useEffect(() => {
    if (spotlight) return;
    const nextSpot = spotQueue.current.shift();
    if (nextSpot) setSpotlight(nextSpot);
  }, [spotlight, spotPing]);

  useEffect(() => {
    if (!spotlight) return;
    const id = setTimeout(() => {
      if (aliveRef.current) setSpotlight(null);
    }, SPOTLIGHT_MS);
    return () => clearTimeout(id);
  }, [spotlight]);

  // Не давать ТВ-экрану гаснуть (best-effort; переполучаем при возврате вкладки).
  useEffect(() => {
    let lock: any = null;
    const acquire = async () => {
      try {
        lock = await (navigator as any).wakeLock?.request("screen");
      } catch {
        /* нет поддержки — игнорируем */
      }
    };
    acquire();
    const onVis = () => document.visibilityState === "visible" && acquire();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try {
        lock?.release?.();
      } catch {
        /* noop */
      }
    };
  }, []);

  const women = rows.filter((r) => r.sex === "F");
  const men = rows.filter((r) => r.sex === "M");

  // Коллективная дистанция всех участников (для строки «все вместе проплыли…»).
  const totalM = rows.reduce((s, r) => s + (Number(r.distance_m) || 0), 0);
  const totalKm =
    totalM >= 100000
      ? String(Math.round(totalM / 1000))
      : String(Math.round(totalM / 100) / 10);

  const timeStr = updatedAt
    ? new Intl.DateTimeFormat(LOCALE[lang], { hour: "2-digit", minute: "2-digit", second: "2-digit" }).format(updatedAt)
    : "—";

  const renderRow = (row: LeaderboardRow, idx: number) => {
    const rank = idx + 1;
    const k = rowKey(row);
    const flash = flashKeys.has(k);
    const tone =
      rank === 1
        ? "bg-gold/10 border-gold/45"
        : rank <= 3
          ? "bg-violet-t12 border-line-3"
          : "bg-card border-line-2";
    const rankTone = rank === 1 ? "text-gold" : rank <= 3 ? "text-violet-lighter" : "text-muted";
    return (
      <div
        key={k}
        className={`flex-1 min-h-0 flex items-center gap-[1.2vw] rounded-[14px] border px-[1.2vw] ${tone}`}
        style={{ maxHeight: "clamp(54px, 8vh, 92px)", ...(flash ? { animation: "lbFlash 1.5s ease-out" } : {}) }}
      >
        <span
          className={`shrink-0 text-center font-display font-bold ${rankTone}`}
          style={{ width: "1.7em", fontSize: "clamp(18px, 2.2vw, 40px)" }}
        >
          {rank}
        </span>
        <span className="flex-1 min-w-0 truncate font-bold" style={{ fontSize: "clamp(15px, 1.7vw, 30px)" }}>
          {row.nickname || t("coach.rowing.guestFallback")}
        </span>
        <span className="shrink-0 text-right leading-none">
          <span className="font-display font-bold" style={{ fontSize: "clamp(18px, 2.2vw, 40px)" }}>
            {Math.round(row.power_w)}
          </span>
          <span className="text-muted font-semibold ml-1" style={{ fontSize: "clamp(10px, 1vw, 18px)" }}>
            {t("unit.watts")}
          </span>
          <div className="text-muted2 font-medium" style={{ fontSize: "clamp(10px, 1vw, 18px)" }}>
            {row.distance_m} {t("unit.meters")}
          </div>
        </span>
      </div>
    );
  };

  const renderColumn = (list: LeaderboardRow[], label: string, accent: string) => (
    <div className="flex flex-col min-h-0">
      <div className="flex items-baseline justify-between mb-[1vh] px-1">
        <span className={`font-display font-bold ${accent}`} style={{ fontSize: "clamp(18px, 2vw, 34px)" }}>
          {label}
        </span>
        <span className="text-muted font-semibold" style={{ fontSize: "clamp(12px, 1.1vw, 20px)" }}>
          {list.length}
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-center gap-[1vh] min-h-0">
        {list.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-muted" style={{ fontSize: "clamp(16px, 1.6vw, 28px)" }}>
            {t("coach.rowing.boardEmpty")}
          </div>
        ) : (
          list.slice(0, TOP_N).map((r, i) => renderRow(r, i))
        )}
      </div>
    </div>
  );

  return (
    <div className="relative h-dvh w-screen overflow-hidden bg-bg text-white flex flex-col px-[3vw] py-[3vh]">
      {/* Шапка */}
      <header className="flex items-center justify-between gap-4 shrink-0 mb-[2vh]">
        <div className="flex items-center gap-[1.4vw] min-w-0">
          <LogoTile size={56} shadow="lg" className="!w-[clamp(40px,4vw,72px)] !h-[clamp(40px,4vw,72px)]" />
          <h1 className="font-display font-bold truncate" style={{ fontSize: "clamp(24px, 3.2vw, 56px)" }}>
            {t("lb.title")}
          </h1>
        </div>
        <div className="flex flex-col items-end gap-[0.6vh] shrink-0">
          <span
            className={`flex items-center gap-2 rounded-full px-[1vw] py-[0.6vh] border ${
              offline ? "bg-white/[0.06] border-white/15 text-muted" : "bg-success/[0.12] border-success/25 text-success"
            }`}
            style={{ fontSize: "clamp(12px, 1.1vw, 20px)" }}
          >
            {!offline && (
              <span
                className="rounded-full bg-success"
                style={{ width: "0.6em", height: "0.6em", boxShadow: "0 0 8px #3FCF6A", animation: "liveDot 1.8s ease-in-out infinite" }}
              />
            )}
            <span className="font-bold uppercase tracking-wide">{offline ? t("lb.offline") : t("lb.live")}</span>
          </span>
          <span className="text-muted" style={{ fontSize: "clamp(11px, 1vw, 18px)" }}>
            {t("lb.updated")} {timeStr} · {t("lb.participants", { n: rows.length })}
          </span>
        </div>
      </header>

      {/* Тело: Ж слева, М справа */}
      {!hasData ? (
        <div className="flex-1" />
      ) : (
        <div className="flex-1 grid grid-cols-2 gap-[2.5vw] min-h-0">
          {renderColumn(women, t("lb.women"), "text-violet-lighter")}
          {renderColumn(men, t("lb.men"), "text-violet-lighter")}
        </div>
      )}

      {/* Коллективный итог: сколько «проплыли» все вместе + крупная аналогия */}
      {hasData && totalM > 0 && (
        <footer className="shrink-0 mt-[1.4vh] text-center">
          <span className="font-semibold" style={{ fontSize: "clamp(13px, 1.35vw, 26px)" }}>
            {t("lb.total", { km: totalKm })}{" "}
            <span className="text-violet-lighter">· {bigDistanceAnalogy(totalM, lang)}</span>
          </span>
        </footer>
      )}

      {/* Спотлайт только что введённого атлета */}
      {spotlight && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-bg/95 text-center px-[6vw]">
          <LogoTile size={64} shadow="lg" />
          <p className="mt-[3vh] text-muted font-semibold uppercase tracking-wide" style={{ fontSize: "clamp(16px, 1.6vw, 28px)" }}>
            {t("lb.spotlight.recorded")}
          </p>
          <h2 className="mt-[1vh] font-display font-bold leading-tight" style={{ fontSize: "clamp(40px, 6vw, 112px)" }}>
            {spotlight.name}
          </h2>
          <div className="mt-[2vh] flex items-end gap-3 justify-center">
            <span className="font-display font-bold text-violet-lighter leading-none" style={{ fontSize: "clamp(48px, 7vw, 132px)" }}>
              {spotlight.power}
            </span>
            <span className="text-muted font-semibold mb-[1.4vh]" style={{ fontSize: "clamp(18px, 2vw, 38px)" }}>
              {t("unit.watts")} · {spotlight.distance} {t("unit.meters")}
            </span>
          </div>
          <p className="mt-[3vh] font-display font-bold text-gold" style={{ fontSize: "clamp(28px, 3.6vw, 72px)" }}>
            {t("lb.spotlight.overall", { rank: spotlight.overallRank })}
          </p>
          <p className="mt-[1.5vh] text-white/85 font-semibold" style={{ fontSize: "clamp(16px, 1.9vw, 34px)" }}>
            {spotlight.analogy}
          </p>
        </div>
      )}
    </div>
  );
}
